import { AreaStack } from '@visx/shape';
import { Group } from '@visx/group';
import { useId } from 'react';
import styled, { useTheme } from 'styled-components';

const GoogleAreaPath = styled.path`
    fill: ${({ theme }) => theme.google_blue_100};
    stroke-linejoin: round;
`;

const GoogleLinePath = styled.path`
    fill: none;
    stroke: ${({ theme }) => theme.google_blue};
    stroke-width: ${({ $strokeWidth }) => $strokeWidth}px;
    stroke-linejoin: round;
`;

const TikTokAreaPath = styled.path`
    fill: ${({ theme }) => theme.tiktok_pink_100};
    stroke-linejoin: round;
`;

const TikTokLinePath = styled.path`
    fill: none;
    stroke: ${({ theme }) => theme.tiktok_pink};
    stroke-width: ${({ $strokeWidth }) => $strokeWidth}px;
    stroke-linejoin: round;
`;

const InstagramAreaPath = styled.path`
    fill: ${({ theme }) =>
        theme.instagram_orange_100 || theme.instagram_orange + '20'};
    stroke-linejoin: round;
`;

const InstagramLinePath = styled.path`
    fill: none;
    stroke: ${({ theme }) => theme.instagram_orange};
    stroke-width: ${({ $strokeWidth }) => $strokeWidth}px;
    stroke-linejoin: round;
`;

const OverviewAreaPath = styled.path`
    fill: ${({ theme }) => theme.purple_50};
    stroke-linejoin: round;
`;

const OverviewLinePath = styled.path`
    fill: none;
    stroke: ${({ theme }) => theme.purple_500};
    stroke-width: ${({ $strokeWidth }) => $strokeWidth}px;
    stroke-linejoin: round;
    stroke-dasharray: 4 4;
`;

// Sources whose backfilled (estimated) prefix is drawn with a hatched area and
// a dashed line instead of the solid observed style. Each maps to its hatch
// fill token and its dashed-line stroke token.
const BACKFILL_SOURCE_COLORS = {
    instagram: {
        hatch: (theme) => theme.instagram_orange_100,
        stroke: (theme) => theme.instagram_orange,
    },
    tiktok: {
        hatch: (theme) => theme.tiktok_pink_100,
        stroke: (theme) => theme.tiktok_pink,
    },
};

const BackfillAreaPath = styled.path`
    stroke-linejoin: round;
`;

const BackfillLinePath = styled.path`
    fill: none;
    stroke: ${({ theme, $dataSource }) =>
        BACKFILL_SOURCE_COLORS[$dataSource]?.stroke(theme)};
    stroke-width: ${({ $strokeWidth }) => $strokeWidth}px;
    stroke-linejoin: round;
    stroke-dasharray: 4 4;
`;

function DataSourcePath({ dataSource, backfill, patternId, ...props }) {
    if (backfill && BACKFILL_SOURCE_COLORS[dataSource])
        return <BackfillAreaPath fill={`url(#${patternId})`} {...props} />;
    if (dataSource === 'google') return <GoogleAreaPath {...props} />;
    if (dataSource === 'tiktok') return <TikTokAreaPath {...props} />;
    if (dataSource === 'instagram') return <InstagramAreaPath {...props} />;
    if (dataSource === 'overview') return <OverviewAreaPath {...props} />;
    throw new Error(`Unknown dataSource: ${dataSource}`);
}

function DataSourceLinePath({ dataSource, backfill, ...props }) {
    if (backfill && BACKFILL_SOURCE_COLORS[dataSource])
        return <BackfillLinePath $dataSource={dataSource} {...props} />;
    if (dataSource === 'google') return <GoogleLinePath {...props} />;
    if (dataSource === 'tiktok') return <TikTokLinePath {...props} />;
    if (dataSource === 'instagram') return <InstagramLinePath {...props} />;
    if (dataSource === 'overview') return <OverviewLinePath {...props} />;
    throw new Error(`Unknown dataSource: ${dataSource}`);
}

function countBackfillPrefix(stack) {
    let count = 0;
    while (
        count < stack.length &&
        stack[count]?.data?.[`${stack.key}_backfill`]
    )
        count += 1;
    return count;
}

function buildLinePath(points, x, y1) {
    return points.reduce((acc, d) => {
        if (!acc) return `M${x(d)},${y1(d)}`;
        return acc + `L${x(d)},${y1(d)}`;
    }, '');
}

function DataSourceAreaStacks({ keys, stackData, x, y0, y1, strokeWidth }) {
    const theme = useTheme();
    const uid = useId();
    const uidSuffix = uid.replace(/:/g, '');
    const hatchIdBySource = Object.fromEntries(
        Object.keys(BACKFILL_SOURCE_COLORS).map((source) => [
            source,
            `backfill-hatch-${source}-${uidSuffix}`,
        ]),
    );

    return (
        <Group>
            <defs>
                {Object.entries(BACKFILL_SOURCE_COLORS).map(
                    ([source, colors]) => (
                        <pattern
                            key={source}
                            id={hatchIdBySource[source]}
                            patternUnits="userSpaceOnUse"
                            width={3}
                            height={3}
                            patternTransform="rotate(45)"
                        >
                            <rect
                                width={3}
                                height={3}
                                fill={colors.hatch(theme)}
                            />
                            <line
                                x1={0}
                                y1={0}
                                x2={0}
                                y2={3}
                                stroke={theme.white}
                                strokeWidth={1}
                            />
                        </pattern>
                    ),
                )}
            </defs>
            <AreaStack
                top={0}
                left={0}
                keys={keys}
                data={stackData}
                x={x}
                y0={y0}
                y1={y1}
            >
                {({ stacks, path }) => {
                    return stacks.map((stack) => {
                        const backfillCount = countBackfillPrefix(stack);
                        if (backfillCount <= 0) {
                            return (
                                <DataSourcePath
                                    key={`stack-${stack.key}`}
                                    dataSource={stack.key}
                                    d={path(stack) || ''}
                                />
                            );
                        }
                        return (
                            <Group key={`stack-${stack.key}`}>
                                <DataSourcePath
                                    dataSource={stack.key}
                                    backfill
                                    patternId={hatchIdBySource[stack.key]}
                                    d={
                                        path(
                                            stack.slice(0, backfillCount + 1),
                                        ) || ''
                                    }
                                />
                                <DataSourcePath
                                    dataSource={stack.key}
                                    d={path(stack.slice(backfillCount)) || ''}
                                />
                            </Group>
                        );
                    });
                }}
            </AreaStack>
            <AreaStack
                top={0}
                left={0}
                keys={keys}
                data={stackData}
                x={x}
                y0={y0}
                y1={y1}
            >
                {({ stacks }) => {
                    return stacks.map((stack) => {
                        const backfillCount = countBackfillPrefix(stack);
                        if (backfillCount <= 0) {
                            return (
                                <DataSourceLinePath
                                    key={`stack-${stack.key}`}
                                    dataSource={stack.key}
                                    $strokeWidth={strokeWidth}
                                    d={buildLinePath(stack, x, y1)}
                                />
                            );
                        }
                        return (
                            <Group key={`stack-${stack.key}`}>
                                <DataSourceLinePath
                                    dataSource={stack.key}
                                    backfill
                                    $strokeWidth={strokeWidth}
                                    d={buildLinePath(
                                        stack.slice(0, backfillCount + 1),
                                        x,
                                        y1,
                                    )}
                                />
                                <DataSourceLinePath
                                    dataSource={stack.key}
                                    $strokeWidth={strokeWidth}
                                    d={buildLinePath(
                                        stack.slice(backfillCount),
                                        x,
                                        y1,
                                    )}
                                />
                            </Group>
                        );
                    });
                }}
            </AreaStack>
        </Group>
    );
}

export default DataSourceAreaStacks;
